import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  TagIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

function articleMainImageSrc(m) {
  if (!m) return null;
  if (typeof m === 'string') return m.startsWith('http') ? m : `${process.env.REACT_APP_API_URL}${m}`;
  if (m.url) return m.url;
  if (m.legacyPath) return `${process.env.REACT_APP_API_URL}${m.legacyPath}`;
  return null;
}

/** Préfixe les src/href en /uploads/… ou /actualites/… pour qu’ils pointent sur l’API (même principe que l’image principale : REACT_APP_API_URL + chemin). */
function prefixMediaInArticleHtml(html) {
  if (!html || typeof html !== 'string') return html;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (!base) return html;
  return html.replace(
    /(src|href)=(["'])(\/(?:uploads|actualites)\/[^"']*)\2/gi,
    (match, attr, q, p) => {
      if (p.startsWith('http') || p.startsWith(base)) return match;
      return `${attr}=${q}${base}${p}${q}`;
    }
  );
}

export default function BlogArticle() {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/actualites/articles/${slug}`,
        { headers }
      );
      setArticle(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'article:', error);
      setError(error.response?.data?.message || 'Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Article non trouvé</h2>
          <p className="text-gray-600 mb-4">{error || 'Cet article n\'existe pas ou vous n\'y avez pas accès.'}</p>
          <button
            onClick={() => navigate('/actualites')}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour au actualites
          </button>
        </div>
      </div>
    );
  }

  const coverSrc = articleMainImageSrc(article.imagePrincipale);

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate('/actualites')}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour aux actualités
          </button>
          {user?.role === 'admin' && article.organisme === user.organisme && (
            <button
              type="button"
              onClick={() => navigate(`/admin/actualites/edit/${article._id}`)}
              className="inline-flex items-center sm:ml-auto px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 shadow-sm"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Modifier
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-12">
        <article className="bg-white shadow rounded-xl overflow-hidden">
          {coverSrc && (
            <img
              src={coverSrc}
              alt={article.titre}
              className="w-full h-auto block"
            />
          )}

          <div className="p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600 mb-2">Actualité</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-snug">
              {article.titre}
            </h1>

            {/* <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
              <span className="inline-flex items-center">
                <UserIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-gray-400" />
                {article.auteur?.prenom} {article.auteur?.nom}
              </span>
              <span className="inline-flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-gray-400" />
                {formatDate(article.datePublication || article.createdAt)}
              </span>
              <span className="inline-flex items-center">
                <EyeIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-gray-400" />
                {article.vues} vues
              </span>
            </div> */}

            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200/80"
                  >
                    <TagIcon className="h-3 w-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {article.extrait && (
              <p className="mt-6 text-lg sm:text-xl text-slate-700 font-medium leading-relaxed border-b border-slate-100 pb-6">
                {article.extrait}
              </p>
            )}

            <div
              className="mt-6 text-gray-800 prose prose-lg max-w-none prose-headings:font-bold prose-a:text-indigo-600"
              dangerouslySetInnerHTML={{ __html: prefixMediaInArticleHtml(article.contenu) }}
            />
          </div>
        </article>
      </div>
    </div>
  );
}
